import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { CaseStudyCard as CaseStudyCardType, CaseStudyTaxonomies } from '@/types/pages';
import { ActiveFilters, default as CaseStudyFilters } from './CaseStudyFilters';
import CaseStudyCard from './CaseStudyCard';
import styles from './CaseStudyList.module.css';

const EMPTY_FILTERS: ActiveFilters = {
  industryId: '',
  offeringId: '',
  technologyId: '',
};

interface Props {
  initialCards: CaseStudyCardType[];
  taxonomies: CaseStudyTaxonomies;
  showFilters?: boolean;
}

const CaseStudyList: React.FC<Props> = ({ initialCards, taxonomies, showFilters = true }) => {
  const [filters, setFilters] = useState<ActiveFilters>(EMPTY_FILTERS);
  const router = useRouter();

  // Deep-link from a case study's taxonomy tags: read the filter from the URL
  // (?industry / ?offering / ?technology) and apply only it, clearing the rest.
  useEffect(() => {
    if (!router.isReady) return;
    const next: ActiveFilters = {
      industryId: (router.query.industry as string) || '',
      offeringId: (router.query.offering as string) || '',
      technologyId: (router.query.technology as string) || '',
    };
    const changed =
      next.industryId !== filters.industryId ||
      next.offeringId !== filters.offeringId ||
      next.technologyId !== filters.technologyId;
    if (changed) {
      setFilters(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.industry, router.query.offering, router.query.technology]);

  const handleFilterChange = (newFilters: ActiveFilters) => {
    setFilters(newFilters);
  };

  const displayedCards = initialCards.filter((card) => {
    const { industryId, offeringId, technologyId } = filters;
    if (!industryId && !offeringId && !technologyId) return true;
    return (
      (industryId && card.industries?.some((i) => i._id === industryId)) ||
      (offeringId && card.offerings?.some((o) => o._id === offeringId)) ||
      (technologyId && card.technologies?.some((t) => t._id === technologyId)) ||
      false
    );
  });

  return (
    <div className="w-full py-10">
      <div className={styles.container}>
        {showFilters && (
          <CaseStudyFilters
            taxonomies={taxonomies}
            active={filters}
            onChange={handleFilterChange}
            onClear={() => handleFilterChange(EMPTY_FILTERS)}
          />
        )}
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {displayedCards.map((card) => (
            <CaseStudyCard key={card._id} {...card} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CaseStudyList;
